import { formatCurrencyAmount, getCurrencyFromCountry, TicketingFormCategory } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { AmplitudeEvents } from "../../../../../common/constants/amplitude";
import { useCurrentUserContext } from "../../../../../common/contexts/CurrentUserContext";
import { useLocaleContext } from "../../../../../common/contexts/LocaleContext";
import { isMax, isMin, isRequired } from "../../../../../common/helpers/validators";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";

import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import type { StepAdvancedSettingsFormData } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/types";

export const AddTargetCard = ({ form }: { form: UseFormReturn<StepAdvancedSettingsFormData> }) => {
  const { t } = useTranslate();
  const { organization } = useCurrentUserContext();
  const { logUserActionEvent, logUserInputEvent } = useLiveFormAmplitudeEvents();
  const { locale } = useLocaleContext();
  const { formCategory } = useLiveFormEditorContext<"ticketing">();

  const {
    control,
    watch,
    formState: { errors },
  } = form;
  const displayThermometer = watch("displayThermometer");

  return (
    <LiveEditorBlock rowGap={1}>
      <LiveEditorToggle
        label={t("dashboard", formCategory === TicketingFormCategory.Lottery ? "salesTarget" : "addTargetAmount")}
        labelVariant="body1"
        name="displayThermometer"
        control={control}
        onValueChange={(checked) => {
          logUserActionEvent(checked ? AmplitudeEvents.FormThermometerAdded : AmplitudeEvents.FormThermometerRemoved);
        }}
      />

      {displayThermometer && (
        <>
          <DashboardTypography variant="body2">{t("dashboard", "targetAmount")}</DashboardTypography>
          <DashboardAmountInput
            name="thermometerTarget"
            control={control}
            locale={locale}
            currency={getCurrencyFromCountry(organization?.country)}
            error={Boolean(errors["thermometerTarget"]?.message)}
            errorMessage={errors["thermometerTarget"]?.message}
            rules={[
              isRequired(t("dashboard", "form.displayThermometer.errorMessage.required")),
              isMin(
                100,
                t("dashboard", "form.displayThermometer.errorMessage.min", {
                  amount: formatCurrencyAmount({
                    value: 1,
                    locale,
                    currency: getCurrencyFromCountry(organization?.country),
                  }),
                })
              ),
              isMax(
                2_000_000_000,
                t("dashboard", "form.displayThermometer.errorMessage.max", {
                  amount: formatCurrencyAmount({
                    value: 2_000_000_000,
                    locale,
                    currency: getCurrencyFromCountry(organization?.country),
                  }),
                })
              ),
            ]}
            onBlur={logUserInputEvent({
              inputName: "thermometerTarget",
              isEmpty: !form.getValues("thermometerTarget"),
              isValid: !errors["thermometerTarget"],
            })}
            sx={{ width: "fit-content" }}
            defaultValue={100000}
          />
        </>
      )}
    </LiveEditorBlock>
  );
};
